// Which Otto version this user last dismissed the What's New deck for.
//
// The truth lives in filter-ai, NOT in localStorage, and that is the whole reason this module exists. The
// app logs out with `localStorage.clear()` (TopBar.jsx, dashboard/index.jsx) — a SPA clear with no reload —
// so a flag kept only in the browser is wiped at every logout and the modal would reappear at every login.
// It would also fail to follow the user to a second machine, announcing 1.6.0 again on their laptop.
//
// localStorage is still used, as a CACHE: it lets the first render answer synchronously (no flash of a
// modal that a late fetch then retracts) while the authoritative value is revalidated behind it. Same
// division of labour as `core/ottoAccess.js`, and the cache is user-scoped for the same reason — two people
// on one machine must not inherit each other's answer.
import { readUserId } from "../core/chatStore";

const CACHE_KEY = "otto_news_seen";

// One client for the two endpoints this module owns, in the same shape as every other filter-ai caller
// here. Each call binds it to a local named `prefsApi` and passes the path as a plain string literal —
// `prefsApi.get("/user_prefs/me")` — because that is the exact shape the otto-sync `xrepo-proxy-paths` gate
// extracts (it matches `<name>Api.get("…")`), and this file is registered in its caller list. Without that,
// `/user_prefs` works locally and returns the SPA's index.html in every deployed environment.
//
// LOADED WITH A DYNAMIC import() ON FIRST USE. The previous version lazily required axios and took
// `.default` or the module itself. That is fine under jest (the suite mocks axios as a plain `{ create }`
// object) and BROKEN IN THE REAL BROWSER: under CRA 5 / webpack 5 the required value has no `create`, so it
// threw "axios.create is not a function", the client came back null, hydrateSeen reported resolved:false,
// and FilterAssistant failed closed. Net effect, verified in the browser on 2026-09-14: `/user_prefs/me` was
// never requested, the What's New deck never appeared, and no dismissal was ever saved — silently, because
// failing closed is indistinguishable from "nothing new to show".
//
// Why not a static `import axios`: axios 1.x is ESM and CRA's jest does not transform node_modules, so every
// suite that renders the panel without mocking axios would fail to load. A dynamic import resolves as a real
// ES module under webpack, and under jest nothing is imported until a call is made. Same loader as
// `src/components/imageAnalysis/imageAnalysisService.js`, where the broken shape was first caught.
// `__tests__/axiosLoading.test.js` fails the build if a lazy axios require appears anywhere in src again.
//
// Resolves to null when the client cannot be built — callers treat null as "unresolved", the same
// fail-closed answer a network error produces: show no deck rather than take the panel down. A failed load
// is not cached, so a transient chunk-load error does not disable the deck for the rest of the session.
let clientPromise = null;
const getPrefsClient = () => {
    if (!clientPromise) {
        clientPromise = Promise.all([import("axios"), import("../../../config/constants"), import("../core/authRetry")])
            .then(([axiosModule, constants, authRetry]) => {
                const axios = axiosModule.default || axiosModule;
                const client = axios.create({ baseURL: constants.FILTER_AI_API_URL, timeout: 15000, withCredentials: true });
                authRetry.attachOttoAuthRetry(client);
                return client;
            })
            .catch(() => {
                clientPromise = null;
                return null;
            });
    }
    return clientPromise;
};

const local = {
    get() {
        try {
            return typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem(CACHE_KEY) : null;
        } catch (e) {
            return null; // absent or throwing storage (incognito/embedded) — degrade, never break a render
        }
    },
    set(value) {
        try {
            if (typeof window !== "undefined" && window.localStorage) window.localStorage.setItem(CACHE_KEY, value);
        } catch (e) {
            /* full or blocked — the backend still has the truth, so this is best-effort */
        }
    }
};

/** The cached answer for the CURRENT user, or null. Never returns another user's record. */
export function readSeenCached() {
    const raw = local.get();
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.userId !== (readUserId() || "anon")) return null;
        return typeof parsed.version === "string" ? parsed.version : null;
    } catch (e) {
        return null; // malformed blob reads as "no record", which just means the deck may show once more
    }
}

function writeCache(version) {
    local.set(JSON.stringify({ userId: readUserId() || "anon", version, at: Date.now() }));
}

// Once this session has decided the user is up to date, it stays decided. Without it, a failed PUT would
// re-pop the modal on the very next page navigation — the user dismissed it, and being asked again three
// seconds later reads as a broken product even though the write is the thing that failed.
let dismissedThisSession = null;
let inFlight = null;
let inFlightFor = null;

/**
 * The authoritative last-seen version.
 *
 * `resolved` is the important half. A failed read returns `resolved: false`, and the caller must then show
 * NOTHING — fail-closed. Late-by-one-session is a much smaller cost than announcing the same release on
 * every page load because the backend is briefly unreachable (or, before FTO-129 deploys, absent).
 */
export function hydrateSeen() {
    const userId = readUserId() || "anon";
    if (inFlight && inFlightFor === userId) return inFlight;

    inFlightFor = userId;
    inFlight = getPrefsClient()
        .then(prefsApi => {
            if (!prefsApi) return { version: readSeenCached(), resolved: false };
            return prefsApi.get("/user_prefs/me").then(res => {
                const news = (res && res.data && res.data.prefs && res.data.prefs.news) || {};
                const version = typeof news.last_seen_version === "string" ? news.last_seen_version : null;
                if (version) writeCache(version);
                return { version, resolved: true };
            });
        })
        .catch(() => ({ version: readSeenCached(), resolved: false }))
        .finally(() => {
            inFlight = null;
            inFlightFor = null;
        });
    return inFlight;
}

/**
 * Record that this user has now seen `version`.
 *
 * Optimistic: the cache and the session guard are updated first, so the UI never waits on the network to
 * close a modal the user has already dismissed. The PUT merges into the user's preference blob rather than
 * replacing it, so a future preference stored alongside `news` survives this write.
 */
export function markSeen(version) {
    if (!version) return Promise.resolve(false);
    dismissedThisSession = version;
    writeCache(version);
    return getPrefsClient()
        .then(prefsApi => {
            if (!prefsApi) return false; // cache + session guard already hold
            return prefsApi
                .put("/user_prefs/me", { prefs: { news: { last_seen_version: version, seen_at: new Date().toISOString() } } })
                .then(() => true);
        })
        .catch(() => false); // the cache and the guard already hold; the next successful read reconciles
}

/** What this session has already dismissed, if anything. */
export function dismissedVersion() {
    return dismissedThisSession;
}

/** Test seam — reset the module-level session state between cases. */
export function __resetNewsSession() {
    clientPromise = null;
    dismissedThisSession = null;
    inFlight = null;
    inFlightFor = null;
}
